import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Calendar as CalendarIcon, MapPin, Image, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreateEvent = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'ride',
    date: null,
    location: '',
    country: 'FR',
    region: '',
    image_url: '',
    max_participants: '',
  });

  const eventTypes = [
    { value: 'concentration', label: t('events.filters.concentration') },
    { value: 'ride', label: t('events.filters.ride') },
    { value: 'meeting', label: t('events.filters.meeting') },
    { value: 'race', label: t('events.filters.race') },
  ];

  const countries = [
    { value: 'FR', label: t('countries.FR') },
    { value: 'DE', label: t('countries.DE') },
    { value: 'ES', label: t('countries.ES') },
    { value: 'IT', label: t('countries.IT') },
    { value: 'BE', label: t('countries.BE') },
    { value: 'CH', label: t('countries.CH') },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.date) {
      toast.error(i18n.language === 'fr' ? 'Veuillez sélectionner une date' : 'Please select a date');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        date: format(formData.date, 'yyyy-MM-dd'),
        max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
      };

      await axios.post(`${API_URL}/events`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(t('common.success'));
      navigate('/events');
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-8 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/events')}
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        data-testid="back-to-events"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('events.title')}
      </button>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading font-bold text-2xl uppercase tracking-tight">
            {t('events.create')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">{i18n.language === 'fr' ? 'Titre' : 'Title'} *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={i18n.language === 'fr' ? 'Balade du dimanche' : 'Sunday ride'}
                className="h-12"
                required
                data-testid="event-title-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={i18n.language === 'fr' ? 'Décrivez votre événement...' : 'Describe your event...'}
                rows={4}
                required
                data-testid="event-description-input"
              />
            </div>

            {/* Event Type & Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select 
                  value={formData.event_type} 
                  onValueChange={(value) => setFormData({ ...formData, event_type: value })}
                >
                  <SelectTrigger className="h-12" data-testid="event-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {eventTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full h-12 justify-start gap-2"
                      data-testid="event-date-picker"
                    >
                      <CalendarIcon className="w-4 h-4" />
                      {formData.date 
                        ? format(formData.date, 'PPP', { locale: i18n.language === 'fr' ? fr : enUS })
                        : i18n.language === 'fr' ? 'Sélectionner une date' : 'Select a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.date}
                      onSelect={(date) => setFormData({ ...formData, date })}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Location & Country */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="location">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  {i18n.language === 'fr' ? 'Lieu' : 'Location'} *
                </Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Paris, France"
                  className="h-12"
                  required
                  data-testid="event-location-input"
                />
              </div>

              <div className="space-y-2">
                <Label>{t('profile.country')} *</Label>
                <Select 
                  value={formData.country} 
                  onValueChange={(value) => setFormData({ ...formData, country: value })}
                >
                  <SelectTrigger className="h-12" data-testid="event-country-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.value} value={country.value}>
                        {country.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Region & Max Participants */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="region">{i18n.language === 'fr' ? 'Région' : 'Region'}</Label>
                <Input
                  id="region"
                  value={formData.region}
                  onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                  placeholder="Île-de-France"
                  className="h-12"
                  data-testid="event-region-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="max_participants">
                  <Users className="w-4 h-4 inline mr-1" />
                  {i18n.language === 'fr' ? 'Participants max' : 'Max participants'}
                </Label>
                <Input
                  id="max_participants"
                  type="number"
                  value={formData.max_participants}
                  onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })}
                  placeholder="50"
                  className="h-12"
                  min="1"
                  data-testid="event-max-participants-input"
                />
              </div>
            </div>

            {/* Image URL */}
            <div className="space-y-2">
              <Label htmlFor="image_url">
                <Image className="w-4 h-4 inline mr-1" />
                {i18n.language === 'fr' ? "URL de l'image" : 'Image URL'}
              </Label>
              <Input
                id="image_url"
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://..."
                className="h-12"
                data-testid="event-image-input"
              />
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => navigate('/events')}
                data-testid="cancel-create-event"
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                className="flex-1 brand-glow font-bold uppercase tracking-wider"
                disabled={loading}
                data-testid="submit-create-event"
              >
                {loading ? t('common.loading') : t('common.save')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateEvent;
